import type * as THREE from 'three'
import type { EngineContext } from '@/runtime/EngineContext'
import type { ViewManagedCamera, ViewRegistry } from '@/shared/types/services'
import type { ViewFrameContext, ViewId, ViewRect, ViewSnapshot } from '@/shared/types/view'

/**
 * 尝试从引擎上下文获取 ViewRegistry。
 *
 * @param context - 引擎上下文
 * @returns ViewRegistry 实例；未注册时返回 null
 */
export function getOptionalViewRegistry(context: EngineContext): ViewRegistry | null {
  if (typeof context.hasService !== 'function' || !context.hasService('ViewRegistry')) {
    return null
  }

  return context.getService('ViewRegistry')
}

/**
 * 获取当前活动视图 ID。
 *
 * @param viewRegistry - ViewRegistry 实例，允许为 null
 * @returns 活动视图 ID；无活动视图返回 null
 */
export function getActiveViewId(viewRegistry: ViewRegistry | null): ViewId | null {
  return viewRegistry?.getActive()?.id ?? null
}

/**
 * 判断当前 view frame 是否属于活动视图。
 *
 * 没有 ViewRegistry 时视为单视口兼容路径，允许继续执行。
 *
 * @param viewRegistry - ViewRegistry 实例，允许为 null
 * @param frame - 当前视图帧上下文
 * @returns 当前帧是否可作为工具插件的活动视图帧
 */
export function isActiveViewFrame(
  viewRegistry: ViewRegistry | null,
  frame: ViewFrameContext
): boolean {
  const activeViewId = getActiveViewId(viewRegistry)
  return !activeViewId || frame.viewId === activeViewId
}

/**
 * 获取当前活动视图快照。
 *
 * @param viewRegistry - ViewRegistry 实例，允许为 null
 * @returns 视图快照；无活动视图返回 null
 */
export function getActiveView(viewRegistry: ViewRegistry | null): ViewSnapshot | null {
  return viewRegistry?.getActive() ?? null
}

/**
 * 获取当前活动视图的相机。
 *
 * @param viewRegistry - ViewRegistry 实例，允许为 null
 * @returns 活动相机；无活动视图或未挂载相机返回 null
 */
export function getActiveViewCamera(viewRegistry: ViewRegistry | null): THREE.Camera | null {
  const activeView = getActiveView(viewRegistry)
  if (!viewRegistry || !activeView) {
    return null
  }

  const managedCamera = viewRegistry.getExtension(activeView.id, 'viewportCamera') as
    | ViewManagedCamera
    | undefined

  return managedCamera?.getActiveCamera() ?? null
}

/**
 * 获取当前活动视图的屏幕矩形。
 *
 * @param viewRegistry - ViewRegistry 实例，允许为 null
 * @returns 视图矩形；无活动视图返回 null
 */
export function getActiveViewRect(viewRegistry: ViewRegistry | null): ViewRect | null {
  return getActiveView(viewRegistry)?.rect ?? null
}

/**
 * 获取指定视图的工具挂载节点，缺省回退到全局 sceneGraph.toolsRoot。
 *
 * @param context - 引擎上下文
 * @param viewRegistry - ViewRegistry 实例，允许为 null
 * @param viewId - 目标视图 ID，允许为 null
 * @returns 用于挂载工具可视化的节点
 */
export function getViewToolsRoot(
  context: EngineContext,
  viewRegistry: ViewRegistry | null,
  viewId: ViewId | null
): THREE.Object3D {
  if (!viewRegistry || !viewId) {
    return context.sceneGraph.toolsRoot
  }

  return (
    (viewRegistry.getExtension(viewId, 'toolsRoot') as THREE.Object3D | null | undefined) ??
    context.sceneGraph.toolsRoot
  )
}

/**
 * 获取指定视图的 Overlay 挂载节点，缺省回退到全局 sceneGraph.overlayRoot。
 *
 * @param context - 引擎上下文
 * @param viewRegistry - ViewRegistry 实例，允许为 null
 * @param viewId - 目标视图 ID，允许为 null
 * @returns 用于挂载 2D/3D Overlay 的节点
 */
export function getViewOverlayRoot(
  context: EngineContext,
  viewRegistry: ViewRegistry | null,
  viewId: ViewId | null
): THREE.Object3D {
  if (!viewRegistry || !viewId) {
    return context.sceneGraph.overlayRoot
  }

  return (
    (viewRegistry.getExtension(viewId, 'overlayRoot') as THREE.Object3D | null | undefined) ??
    context.sceneGraph.overlayRoot
  )
}
